import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { GridFilterModel } from '@mui/x-data-grid';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';

import { useFileFolderSelection } from '../../components/Providers/FileFolderSelectionProvider';
import { getIndexedFiles } from '../../redux/actions/indexActions';
import { selectIndexes } from '../../redux/reducers/indexReducer';
import { IndexType } from '../../Types/enums';
import { CustomFile } from '../../Types/file';
import FileList from './FileList';
import FolderList from './FolderList';

interface FileFolderSelectionModalProps {
  open: boolean;
  indexId: string;
  indexType: IndexType;
  onClose: () => void;
  onConfirm: (selectedFiles: CustomFile[]) => void;
}

const FileFolderSelectionModal: React.FC<FileFolderSelectionModalProps> = ({
  open,
  onClose,
  indexId,
  indexType,
  onConfirm,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [fileSearchQuery, setFileSearchQuery] = useState('');
  const [folderSearchQuery, setFolderSearchQuery] = useState('');
  const dispatch = useDispatch<AppDispatch>();
  const { indexFiles } = useSelector(selectIndexes);
  const { selectedFiles, selectedFolder, clearSelection } = useFileFolderSelection();

  useEffect(() => {
    if (!open) return;

    const getFilters = (): GridFilterModel | undefined => {
      if (tabIndex === 0 && folderSearchQuery) {
        return {
          items: [
            {
              field: 'relativePath',
              operator: 'contains',
              value: folderSearchQuery,
            },
          ],
        };
      }

      if (tabIndex === 1 && fileSearchQuery) {
        return {
          items: [
            {
              field: 'fileName',
              operator: 'contains',
              value: fileSearchQuery,
            },
          ],
        };
      }

      return undefined;
    };

    const filters = getFilters();
    dispatch(
      getIndexedFiles({
        indexId,
        indexType,
        filterModel: filters,
      })
    );

    clearSelection();
  }, [
    open,
    dispatch,
    indexId,
    indexType,
    tabIndex,
    fileSearchQuery,
    folderSearchQuery,
    clearSelection,
  ]);

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setTabIndex(newValue);
      clearSelection();
    },
    [clearSelection]
  );

  const handleConfirm = useCallback(() => {
    onConfirm(selectedFiles);
    onClose();
  }, [selectedFiles, onConfirm, onClose]);

  const isConfirmDisabled = selectedFiles.length === 0 && selectedFolder === null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Select Files or Folder</DialogTitle>
      <DialogContent>
        <Tabs value={tabIndex} onChange={handleTabChange}>
          <Tab
            label={
              <Typography variant="subtitle1" fontWeight="bold">
                Folders
              </Typography>
            }
          />
          <Tab
            label={
              <Typography variant="subtitle1" fontWeight="bold">
                Files
              </Typography>
            }
          />
        </Tabs>

        <Box mt={2}>
          {tabIndex === 0 ? (
            <FolderList
              files={indexFiles}
              onSearch={setFolderSearchQuery}
              searchQuery={folderSearchQuery}
            />
          ) : (
            <FileList
              files={indexFiles}
              onSearch={setFileSearchQuery}
              searchQuery={fileSearchQuery}
            />
          )}
        </Box>

        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ ml: 2 }}
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            Confirm
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FileFolderSelectionModal;
